import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import '@fontsource/chivo-mono';
import '@fontsource/public-sans';
import '@fontsource/jetbrains-mono';
import './App.css';

// Pages (code-split: each route loads its own chunk)
import Login from './pages/Login';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreateTask = lazy(() => import('./pages/CreateTask'));
const ScanParcels = lazy(() => import('./pages/ScanParcels'));
const ManageTasks = lazy(() => import('./pages/ManageTasks'));
const IncomingMaster = lazy(() => import('./pages/IncomingMaster'));
const FlaggedParcels = lazy(() => import('./pages/FlaggedParcels'));
const ProcessingScan = lazy(() => import('./pages/ProcessingScan'));
const PackingScan = lazy(() => import('./pages/PackingScan'));
const ParcelReco = lazy(() => import('./pages/ParcelReco'));
const IncomingDashboard = lazy(() => import('./pages/IncomingDashboard'));
const OutgoingDashboard = lazy(() => import('./pages/OutgoingDashboard'));
const Reports = lazy(() => import('./pages/Reports'));
const ReportExport = lazy(() => import('./pages/ReportExport'));
const Analysis = lazy(() => import('./pages/Analysis'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const CourierMaster = lazy(() => import('./pages/CourierMaster'));
const TrackingSearch = lazy(() => import('./pages/TrackingSearch'));
const CreateManifest = lazy(() => import('./pages/CreateManifest'));
const OutgoingExpected = lazy(() => import('./pages/OutgoingExpected'));
const SsoLogin = lazy(() => import('./pages/SsoLogin'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-100" data-testid="route-loading">
    <div className="spinner" />
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null, requireTaskCreate = false, requireProcessing = false, requirePacking = false }) => {
  const { user, loading, canCreateTasks, canProcessing, canPacking } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireTaskCreate && !canCreateTasks()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireProcessing && !canProcessing()) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requirePacking && !canPacking()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Route - redirects to dashboard if already logged in
const AuthRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <ErrorBoundary>
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <AuthRoute>
            <Login />
          </AuthRoute>
        } 
      />

      {/* Operations SSO landing (one-time code) */}
      <Route path="/sso" element={<SsoLogin />} />

      {/* Protected Routes - All Users */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/new" 
        element={
          <ProtectedRoute requireTaskCreate>
            <CreateTask />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/:taskId/scan" 
        element={
          <ProtectedRoute>
            <ScanParcels />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/:taskId/manifest" 
        element={
          <ProtectedRoute>
            <CreateManifest />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/incoming/dashboard" 
        element={
          <ProtectedRoute>
            <IncomingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outgoing/dashboard" 
        element={
          <ProtectedRoute>
            <OutgoingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outgoing/processing" 
        element={
          <ProtectedRoute requireProcessing>
            <ProcessingScan />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outgoing/packing" 
        element={
          <ProtectedRoute requirePacking>
            <PackingScan />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes - Manager, Admin & Dispatch Executive */}
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager', 'dispatch_executive']}>
            <ManageTasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/incoming/master" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <IncomingMaster />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outgoing/expected" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <OutgoingExpected />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/incoming/flagged" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <FlaggedParcels />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/outgoing/reco" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <ParcelReco />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/export" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <ReportExport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analysis" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <Analysis />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes - Admin Only */}
      <Route 
        path="/users" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/couriers" 
        element={
          <ProtectedRoute allowedRoles={['admin', 'manager']}>
            <CourierMaster />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/search" 
        element={
          <ProtectedRoute>
            <TrackingSearch />
          </ProtectedRoute>
        } 
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </ErrorBoundary>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: 'white',
              border: '2px solid #E2E8F0',
              borderRadius: 0,
              fontFamily: 'Public Sans, sans-serif'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
